import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { useDataStore, DataStore, SalesDataTimePeriod } from "../../data-store";
import { formatTimePeriod } from "../../util/timePeriodFormatting";
import {
  useOrganization,
  useOrganizationId,
} from "../organizations/OrganizationProvider";
import OrganizationProvider from "../organizations/OrganizationProvider";
import Page from "../Page";
import { usePages } from "../pages";
import { SecondaryButton, PrimaryButton } from "../utils/Button";
import Card from "../utils/Card";
import ErrorAlert from "../utils/ErrorAlert";
import FileInputButton from "../utils/FileInputButton";
import Select from "../utils/Select";
import { ParsedSalesDataRow, parseSalesDataCsv } from "./sales-data-csv";
import {
  ColumnDescription,
  columnDescriptions,
  getDefaultColumnName,
} from "./sales-data-csv-columns";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const QUARTER_NAMES = ["Q1", "Q2", "Q3", "Q4"];

function UploadCard({ children }: { children: React.ReactNode }) {
  return (
    <Card style={{ maxWidth: "900px" }} className="p-4">
      {children}
    </Card>
  );
}

export default function SalesDataUploadPage() {
  const pages = usePages();

  return (
    <Page>
      <div className="d-flex flex-column h-100">
        <Page.Title breadcrumb={pages.SalesDataUpload.breadcrumb()} />
        <div className="flex-fit mt-3">
          <OrganizationProvider>
            <SalesDataUploadPageContent />
          </OrganizationProvider>
        </div>
      </div>
    </Page>
  );
}

interface ProductStatus {
  recipeId: number | null;
  code: string | null;
  isValid: boolean;
  recipeName?: string;
  errorType?:
    | "id_not_found"
    | "code_not_found"
    | "mismatch"
    | "missing_identifiers";
}

type State =
  | {
      type: "inactive";
    }
  | { type: "readingFile" }
  | { type: "validatingProductCodes" }
  | {
      type: "parsedData";
      rows: Array<ParsedSalesDataRow>;
      productStatuses: Array<ProductStatus>;
      selectedTimePeriod: SalesDataTimePeriod | null;
    }
  | { type: "uploading" }
  | {
      type: "uploadSuccess";
      recordsCreated: number;
      fileId: string;
    }
  | {
      type: "error";
      error: unknown;
    };

async function fetchRecipes(
  dataStore: DataStore,
  organizationId: string
): Promise<Array<{ name: string; id: number; code: string }>> {
  const response = await dataStore.fetchRecipes(organizationId);
  return response.recipes;
}

function getDefaultTimePeriod() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const defaultMonth = currentMonth === 1 ? 12 : currentMonth - 1;
  const defaultYear = currentMonth === 1 ? currentYear - 1 : currentYear;

  return { defaultMonth, defaultYear };
}

function createTimePeriod(
  selectedYear: number | null,
  periodType: string | null,
  selectedQuarter: number | null,
  selectedMonth: number | null
): SalesDataTimePeriod | null {
  if (!selectedYear || !periodType) return null;

  if (periodType === "Yearly") {
    return { year: selectedYear, periodType: "Yearly" };
  } else if (periodType === "Quarterly" && selectedQuarter) {
    return {
      year: selectedYear,
      quarter: selectedQuarter,
      periodType: "Quarterly",
    };
  } else if (periodType === "Monthly" && selectedMonth) {
    return { year: selectedYear, month: selectedMonth, periodType: "Monthly" };
  }

  return null;
}

function SalesDataUploadPageContent() {
  const { defaultMonth, defaultYear } = getDefaultTimePeriod();
  const intl = useIntl();

  const [state, setState] = useState<State>({ type: "inactive" });
  const [selectedYear, setSelectedYear] = useState<number | null>(defaultYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(
    defaultMonth
  );
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(1);
  const dataStore = useDataStore();
  const [organizationId] = useOrganizationId();
  const [organization] = useOrganization();
  const pages = usePages();
  const history = useHistory();

  const periodType = organization.salesDataUploadPeriod;
  const selectedTimePeriod = createTimePeriod(
    selectedYear,
    periodType,
    selectedQuarter,
    selectedMonth
  );

  const nextYear = new Date().getFullYear() + 1;
  const availableYears = Array.from(
    { length: nextYear - 2010 },
    (_, i) => nextYear - i
  );

  const yearOptions = availableYears.map((year) => ({
    value: year,
    label: year.toString(),
  }));
  const monthOptions = MONTH_NAMES.map((name, index) => ({
    value: index + 1,
    label: name,
  }));
  const quarterOptions = QUARTER_NAMES.map((name, index) => ({
    value: index + 1,
    label: name,
  }));

  const selectedYearOption =
    yearOptions.find((option) => option.value === selectedYear) || null;
  const selectedMonthOption =
    monthOptions.find((option) => option.value === selectedMonth) || null;
  const selectedQuarterOption =
    quarterOptions.find((option) => option.value === selectedQuarter) || null;

  async function handleFileChange(file: File) {
    if (!selectedTimePeriod) {
      return;
    }

    try {
      setState({ type: "readingFile" });
      const text = await file.text();
      const parseResult = parseSalesDataCsv(text);

      setState({ type: "validatingProductCodes" });

      const recipes = await fetchRecipes(dataStore, organizationId);

      const recipesByCode = new Map<string, { name: string; id: number }>();
      const recipesById = new Map<number, { name: string; code: string }>();

      for (const recipe of recipes) {
        recipesByCode.set(recipe.code, { name: recipe.name, id: recipe.id });
        recipesById.set(recipe.id, { name: recipe.name, code: recipe.code });
      }

      const uniqueProductIdentifiers = new Map<string, ProductStatus>();

      // check for various types of errors and also update parseResult.parsedRows
      // so that every row (unless missing identifiers) has a productId and productCode (which could be null)
      for (const row of parseResult.parsedRows) {
        const key = JSON.stringify({
          id: row.productId,
          code: row.productCode,
        });

        if (!uniqueProductIdentifiers.has(key)) {
          let isValid = false;
          let recipeName: string | undefined;
          let errorType:
            | "id_not_found"
            | "code_not_found"
            | "mismatch"
            | "missing_identifiers"
            | undefined;

          if (row.productId !== null && row.productCode !== null) {
            const recipeById = recipesById.get(row.productId);
            const recipeByCode = recipesByCode.get(row.productCode);

            if (!recipeById) {
              isValid = false;
              errorType = "id_not_found";
            } else if (!recipeByCode) {
              isValid = false;
              errorType = "code_not_found";
            } else if (recipeById.code !== row.productCode) {
              isValid = false;
              errorType = "mismatch";
            } else {
              isValid = true;
              recipeName = recipeById.name;
            }
          } else if (row.productId) {
            // ID but no code
            const recipe = recipesById.get(row.productId);
            if (recipe) {
              isValid = true;
              recipeName = recipe.name;
              row.productCode = recipe.code;
            } else {
              isValid = false;
              errorType = "id_not_found";
            }
          } else if (row.productCode) {
            // Code but no ID
            const recipe = recipesByCode.get(row.productCode);
            if (recipe) {
              isValid = true;
              recipeName = recipe.name;
              row.productId = recipe.id;
            } else {
              isValid = false;
              errorType = "code_not_found";
            }
          } else {
            isValid = false;
            errorType = "missing_identifiers";
          }

          uniqueProductIdentifiers.set(key, {
            recipeId: row.productId,
            code: row.productCode,
            isValid,
            recipeName,
            errorType,
          });
        }
      }

      const productStatuses = Array.from(uniqueProductIdentifiers.values());

      setState({
        type: "parsedData",
        rows: parseResult.parsedRows,
        productStatuses,
        selectedTimePeriod,
      });
    } catch (error: unknown) {
      setState({ type: "error", error });
    }
  }

  async function handleUpload() {
    if (state.type !== "parsedData" || !state.selectedTimePeriod) {
      return;
    }

    try {
      setState({ type: "uploading" });

      const validRows = state.rows.filter((row) => {
        const productStatus = state.productStatuses.find(
          (status) =>
            status.recipeId === row.productId && status.code === row.productCode
        );
        return productStatus?.isValid;
      });

      const salesData = validRows.map((row) => ({
        product_id: row.productId!,
        quantity_sold: row.quantitySold,
        price_per_item: row.pricePerItem || undefined,
      }));

      const result = await dataStore.uploadSalesData(
        organizationId,
        state.selectedTimePeriod,
        salesData,
        "uploaded_sales_data.csv"
      );

      setState({
        type: "uploadSuccess",
        recordsCreated: result.records_created,
        fileId: result.file_id,
      });
    } catch (error: unknown) {
      setState({ type: "error", error });
    }
  }

  const handleDone = () => {
    history.push(pages.SalesData.url);
  };

  if (!periodType) {
    return (
      <UploadCard>
        <div className="alert alert-warning">
          <h5>
            <FormattedMessage
              id="components/sales-data/SalesDataUploadPage:salesDataUploadPeriodNotConfigured"
              defaultMessage="Sales Data Upload Period Not Configured"
            />
          </h5>
          <p>
            <FormattedMessage
              id="components/sales-data/SalesDataUploadPage:salesDataUploadPeriodNotConfiguredDescription"
              defaultMessage="Your account needs the sales data upload period to be set before you can upload sales data. Please contact support to configure this setting."
            />
          </p>
        </div>
      </UploadCard>
    );
  }

  if (state.type === "error") {
    return (
      <UploadCard>
        <ErrorAlert error={state.error} />
        <div className="mt-3">
          <SecondaryButton onClick={() => setState({ type: "inactive" })}>
            <FormattedMessage
              id="components/sales-data/SalesDataUploadPage:backToUpload"
              defaultMessage="Back to Upload"
            />
          </SecondaryButton>
        </div>
      </UploadCard>
    );
  }

  if (state.type === "validatingProductCodes") {
    return (
      <UploadCard>
        <FormattedMessage
          id="components/sales-data/SalesDataUploadPage:validatingProductCodes"
          defaultMessage="Validating product codes..."
        />
      </UploadCard>
    );
  }

  if (state.type === "parsedData") {
    const invalidProductCodes = state.productStatuses.filter(
      (status) => !status.isValid
    );
    const hasInvalidCodes = invalidProductCodes.length > 0;

    let salesCount = 0;
    const uniqueRecipeIds = new Set<number>();
    state.rows.forEach((row) => {
      const productStatus = state.productStatuses.find(
        (status) =>
          status.recipeId === row.productId && status.code === row.productCode
      );

      if (productStatus?.isValid && productStatus.recipeId !== null) {
        salesCount += row.quantitySold;
        uniqueRecipeIds.add(productStatus.recipeId);
      }
    });

    const uniqueProducts = uniqueRecipeIds.size;

    let timePeriodLabel = "";
    if (state.selectedTimePeriod) {
      timePeriodLabel = formatTimePeriod(state.selectedTimePeriod, intl);
    }

    return (
      <UploadCard>
        <p>
          <FormattedMessage
            id="components/sales-data/SalesDataUploadPage:identifiedSales"
            defaultMessage="Identified <b>{salesCount, number} sales</b> for <b>{productCount, number} products</b> in <b>{timePeriod}</b>."
            values={{
              salesCount,
              productCount: uniqueProducts,
              timePeriod: timePeriodLabel,
              b: (chunks: React.ReactNode) => <b>{chunks}</b>,
            }}
          />
        </p>

        {hasInvalidCodes && (
          <div className="alert alert-warning mt-3">
            <h5>
              <FormattedMessage
                id="components/sales-data/SalesDataUploadPage:invalidProductCodesTitle"
                defaultMessage="Invalid Product Identifiers Found"
              />
            </h5>
            <p>
              <FormattedMessage
                id="components/sales-data/SalesDataUploadPage:invalidProductCodesDescription"
                defaultMessage="The following product identifiers in your sales data have errors:"
              />
            </p>
            <ul>
              {invalidProductCodes.map((status) => (
                <li key={status.recipeId || status.code}>
                  {status.errorType === "mismatch" ? (
                    <FormattedMessage
                      id="components/sales-data/SalesDataUploadPage:mismatchError"
                      defaultMessage="<code>ID: {recipeId}</code> and <code>Code: {productCode}</code> do not match the same recipe"
                      values={{
                        recipeId: status.recipeId,
                        productCode: status.code,
                        code: (chunks: React.ReactNode) => (
                          <code>{chunks}</code>
                        ),
                      }}
                    />
                  ) : status.errorType === "id_not_found" ? (
                    <FormattedMessage
                      id="components/sales-data/SalesDataUploadPage:idNotFoundError"
                      defaultMessage="<code>ID: {recipeId}</code> does not exist in your database."
                      values={{
                        recipeId: status.recipeId,
                        code: (chunks: React.ReactNode) => (
                          <code>{chunks}</code>
                        ),
                      }}
                    />
                  ) : status.errorType === "code_not_found" ? (
                    <FormattedMessage
                      id="components/sales-data/SalesDataUploadPage:codeNotFoundError"
                      defaultMessage="<code>Code: {productCode}</code> does not exist in your database."
                      values={{
                        productCode: status.code,
                        code: (chunks: React.ReactNode) => (
                          <code>{chunks}</code>
                        ),
                      }}
                    />
                  ) : (
                    <code>
                      {status.recipeId !== null
                        ? `ID: ${status.recipeId}`
                        : `Code: ${status.code}`}
                    </code>
                  )}
                </li>
              ))}
            </ul>
            <p>
              <FormattedMessage
                id="components/sales-data/SalesDataUploadPage:invalidProductCodesAction"
                defaultMessage="Please fix these errors before uploading."
              />
            </p>
          </div>
        )}

        {!hasInvalidCodes && (
          <FormattedMessage
            id="components/sales-data/SalesDataUploadPage:allProductCodesValid"
            defaultMessage="All product identifiers are valid and match recipes in your account."
          />
        )}

        <div className="mt-3">
          <div className="d-flex">
            <SecondaryButton
              onClick={() => setState({ type: "inactive" })}
              className="mr-2"
            >
              <FormattedMessage
                id="components/sales-data/SalesDataUploadPage:cancel"
                defaultMessage="Cancel"
              />
            </SecondaryButton>
            {!hasInvalidCodes && (
              <PrimaryButton onClick={handleUpload}>
                <FormattedMessage
                  id="components/sales-data/SalesDataUploadPage:next"
                  defaultMessage="Next"
                />
              </PrimaryButton>
            )}
          </div>
        </div>
      </UploadCard>
    );
  }

  if (state.type === "uploading") {
    return (
      <UploadCard>
        <FormattedMessage
          id="components/sales-data/SalesDataUploadPage:uploading"
          defaultMessage="Uploading sales data..."
        />
      </UploadCard>
    );
  }

  if (state.type === "uploadSuccess") {
    return (
      <UploadCard>
        <div>
          <FormattedMessage
            id="components/sales-data/SalesDataUploadPage:uploadSuccessMessage"
            defaultMessage="Sales data has successfully been uploaded."
          />
        </div>
        <div className="mt-3">
          <PrimaryButton onClick={handleDone}>
            <FormattedMessage
              id="components/sales-data/SalesDataUploadPage:done"
              defaultMessage="Done"
            />
          </PrimaryButton>
        </div>
      </UploadCard>
    );
  }

  return (
    <UploadCard>
      <div className="form-group mb-3">
        <label className="form-label">
          <h4>
            <FormattedMessage
              id="components/sales-data/SalesDataUploadPage:salesPeriodLabel"
              defaultMessage="Select a sales period"
            />
          </h4>
        </label>
        <div className="d-flex">
          {periodType === "Monthly" && (
            <div style={{ minWidth: "160px", marginRight: "8px" }}>
              <Select
                id="monthSelect"
                options={monthOptions}
                renderOption={(option) => option.label}
                optionKey={(option) => option.value.toString()}
                value={selectedMonthOption}
                onChange={(option) => {
                  if (option) {
                    setSelectedMonth(option.value);
                  } else {
                    setSelectedMonth(null);
                  }
                }}
                isClearable={false}
              />
            </div>
          )}

          {periodType === "Quarterly" && (
            <div style={{ minWidth: "100px", marginRight: "8px" }}>
              <Select
                id="quarterSelect"
                options={quarterOptions}
                renderOption={(option) => option.label}
                optionKey={(option) => option.value.toString()}
                value={selectedQuarterOption}
                onChange={(option) => {
                  if (option) {
                    setSelectedQuarter(option.value);
                  } else {
                    setSelectedQuarter(null);
                  }
                }}
                isClearable={false}
              />
            </div>
          )}

          <div
            style={{
              minWidth: "120px",
            }}
          >
            <Select
              id="yearSelect"
              options={yearOptions}
              renderOption={(option) => option.label}
              optionKey={(option) => option.value.toString()}
              value={selectedYearOption}
              onChange={(option) => {
                if (option) {
                  setSelectedYear(option.value);
                } else {
                  setSelectedYear(null);
                }
              }}
              isClearable={false}
              placeholder={
                periodType === "Yearly" ? (
                  <FormattedMessage
                    id="components/sales-data/SalesDataUploadPage:selectYear"
                    defaultMessage="Select a year..."
                  />
                ) : undefined
              }
            />
          </div>
        </div>
      </div>
      <p>
        <FormattedMessage
          id="components/sales-data/SalesDataUploadPage:uploadPrompt"
          defaultMessage="Select a CSV containing the sales you want to upload for the sales period."
        />
      </p>

      <div className="form-group">
        <FileInputButton onChange={handleFileChange} accept=".csv" />
        {!selectedTimePeriod && (
          <div className="form-text alert alert-warning">
            <FormattedMessage
              id="components/sales-data/SalesDataUploadPage:selectTimePeriodFirst"
              defaultMessage="Please select a time period before uploading a file."
            />
          </div>
        )}
      </div>
      <h5 className="medium-font mt-4">
        <FormattedMessage
          id="components/sales-data/SalesDataUploadPage:format"
          defaultMessage="Format"
        />
      </h5>
      <p>
        <FormattedMessage
          id="components/sales-data/SalesDataUploadPage:csvColumnNameInstructions"
          defaultMessage="The CSV should have the following column names (as seen below in bold):"
        />
      </p>
      {columnDescriptions.map((columnDescription: ColumnDescription) => {
        return (
          <dl className="row mb-1" key={columnDescription.id}>
            <dt className="col-2 medium-font">
              {getDefaultColumnName(columnDescription)}
            </dt>
            <dd className="col-10">{columnDescription.description}</dd>
          </dl>
        );
      })}
      <p className="mt-2">
        <FormattedMessage
          id="components/sales-data/SalesDataUploadPage:asteriskNote"
          defaultMessage="*Each row must include either the Product ID, the Product code, or both."
        />
      </p>
      <p className="mt-3">
        <a
          href="/assets/upload_sales_data_example.csv"
          download="foodsteps-upload-sales-data-example.csv"
        >
          <FormattedMessage
            id="components/sales-data/SalesDataUploadPage:downloadExampleCsv"
            defaultMessage="Download an example CSV"
          />
        </a>
      </p>
    </UploadCard>
  );
}
